// src/components/VatReturn.js
// VAT Return: Output VAT (sales) vs Input VAT (purchases) for a period, with the
// net payable/refundable to the revenue authority (KRA VAT3 shape).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, Alert, CircularProgress, IconButton, Grid, Divider
} from '@mui/material';
import { Home, Print, ReceiptLong, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const todayStr    = () => new Date().toISOString().split('T')[0];

const VatTable = ({ title, rows, color }) => (
  <>
    <Typography variant="subtitle1" fontWeight={800} sx={{ color, mt: 2, mb: 1 }}>{title}</Typography>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell><strong>VAT Group</strong></TableCell>
          <TableCell align="right"><strong>Rate %</strong></TableCell>
          <TableCell align="right"><strong>Net (Base)</strong></TableCell>
          <TableCell align="right"><strong>VAT</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">None</Typography></TableCell></TableRow>
        ) : rows.map((r, i) => (
          <TableRow key={r.vatGroupCode + i} hover>
            <TableCell>{r.vatGroupCode}</TableCell>
            <TableCell align="right">{r.vatPercent}%</TableCell>
            <TableCell align="right">{fmt(r.base)}</TableCell>
            <TableCell align="right">{fmt(r.vatAmount)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </>
);

const VatReturn = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [from, setFrom]       = useState(firstOfYear());
  const [to, setTo]           = useState(todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/reports/vat-return`, { headers: hdrs(), params: { from, to } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load VAT return'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportExcel = () => {
    if (!data) return;
    const t = data.totals;
    const rows = [
      [company?.name || 'VAT RETURN'],
      ['VAT RETURN'],
      [`Period: ${from} to ${to}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['OUTPUT VAT (Sales)'],
      ['VAT Group', 'Rate %', 'Net (Base)', 'VAT'],
      ...data.outputRows.map(r => [r.vatGroupCode, r.vatPercent, r.base, r.vatAmount]),
      ['', '', t.outputBase, t.outputVat],
      [],
      ['INPUT VAT (Purchases)'],
      ['VAT Group', 'Rate %', 'Net (Base)', 'VAT'],
      ...data.inputRows.map(r => [r.vatGroupCode, r.vatPercent, r.base, r.vatAmount]),
      ['', '', t.inputBase, t.inputVat],
      [],
      ['Output VAT', t.outputVat],
      ['Input VAT', t.inputVat],
      [t.netPayable >= 0 ? 'Net VAT Payable' : 'Net VAT Refundable', Math.abs(t.netPayable)],
    ];
    downloadCsv(rows, `vat_return_${from}_to_${to}.csv`);
  };

  const t = data?.totals;
  const payable = t ? t.netPayable >= 0 : true;

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .vat-card { box-shadow:none !important; } }`}</style>
      <Box display="flex" gap={1.5} mb={2} className="no-print" alignItems="center" flexWrap="wrap">
        <TextField type="date" label="From" value={from} size="small"
          onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="To" value={to} size="small"
          onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#e6510015' }}>
          {loading ? <CircularProgress size={20} /> : <ReceiptLong />}
        </IconButton>
        <Box flex={1} />
        <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
          sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
        <IconButton onClick={() => window.print()} title="Print" disabled={!data}
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
        <IconButton onClick={() => navigate('/finance')} title="Finance Home"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3} className="vat-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="VAT RETURN" subtitle={`${from} → ${to}`} />
          {data && (
            <>
              <VatTable title="Output VAT (Sales)" rows={data.outputRows} color="#2e7d32" />
              <VatTable title="Input VAT (Purchases)" rows={data.inputRows} color="#c62828" />
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Output VAT</Typography>
                    <Typography variant="h6" fontWeight="bold">{sym()} {fmt(t.outputVat)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Input VAT</Typography>
                    <Typography variant="h6" fontWeight="bold">{sym()} {fmt(t.inputVat)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, bgcolor: payable ? 'error.light' : 'success.light', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {payable ? 'Net VAT Payable' : 'Net VAT Refundable'}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">{sym()} {fmt(Math.abs(t.netPayable))}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VatReturn;
